import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Box, Typography, Card, CardContent, Skeleton, Alert, Tooltip, IconButton } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import api from "../../utils/api";
import { setWithExpiry, getWithExpiry } from "../../utils/cache";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";

// Pre-built once. shared across all markers, never recreated on re-render
const MARKER_ICONS = {
  error: new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    shadowUrl: markerShadowPng,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
    shadowSize: [41, 41], shadowAnchor: [12, 41],
  }),
  warning: new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
    shadowUrl: markerShadowPng,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
    shadowSize: [41, 41], shadowAnchor: [12, 41],
  }),
  secondary: new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png",
    shadowUrl: markerShadowPng,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
    shadowSize: [41, 41], shadowAnchor: [12, 41],
  }),
  success: new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
    shadowUrl: markerShadowPng,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
    shadowSize: [41, 41], shadowAnchor: [12, 41],
  }),
  default: new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
    shadowUrl: markerShadowPng,
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
    shadowSize: [41, 41], shadowAnchor: [12, 41],
  }),
};

const LEGEND_ITEMS = [
  { key: "success",   hex: "#2e7d32", label: "Active"     },
  { key: "warning",   hex: "#ed6c02", label: "Warning"    },
  { key: "error",     hex: "#d32f2f", label: "Violation"  },
  { key: "secondary", hex: "#757575", label: "Inactive"   },
  { key: "default",   hex: "#2f63ad", label: "Standard"   },
];

const STATUS_STYLES = {
  success:   { label: "Active",     color: "#2e7d32", bg: "rgba(46,125,50,0.10)"   },
  warning:   { label: "Warning",    color: "#ed6c02", bg: "rgba(237,108,2,0.10)"   },
  error:     { label: "Violation",  color: "#d32f2f", bg: "rgba(211,47,47,0.10)"   },
  secondary: { label: "Inactive",   color: "#757575", bg: "rgba(117,117,117,0.10)" },
  default:   { label: "Standard",   color: "#2f63ad", bg: "rgba(47,99,173,0.10)"   },
};

const MapBoundsController = ({ locations, fitRef }) => {
  const map = useMap();

  const fitAll = () => {
    if (locations.length === 0) return;
    const bounds = L.latLngBounds(locations.map((loc) => [loc.lat, loc.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  };

  useEffect(() => {
    fitAll();
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose fitAll so the reset button can call it
  useEffect(() => {
    if (fitRef) fitRef.current = fitAll;
  });

  return null;
};

const OpenStreetMapComponent = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fitRef = useRef(null);

  useEffect(() => {
    const cached = getWithExpiry("member-locations");
    if (cached) {
      setLocations(cached);
      setLoading(false);
      return;
    }

    api
      .get("/member-locations")
      .then((res) => {
        const validLocations = (res.data.locations || []).filter(
          (loc) => loc.lat && loc.lng
        );
        setLocations(validLocations);
        setWithExpiry("member-locations", validLocations, 86400000);
      })
      .catch((err) => {
        console.error("Error loading locations:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card
      sx={{
        borderRadius: "18px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        backgroundColor: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(6px)",
        border: "1px solid rgba(47,99,173,0.08)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header + legend row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", letterSpacing: "0.2px" }}
            >
              Active Members Map{!loading && !error ? ` (${locations.length})` : ""}
            </Typography>
            {!loading && !error && (
              <Tooltip title="Fit all markers">
                <IconButton
                  size="small"
                  onClick={() => fitRef.current?.()}
                  sx={{
                    color: "#2f63ad",
                    backgroundColor: "rgba(47,99,173,0.08)",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "rgba(47,99,173,0.16)" },
                  }}
                >
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {!loading && !error && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {LEGEND_ITEMS.map(({ hex, label }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: hex,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={500} sx={{ borderRadius: "14px" }} />
            <Typography sx={{ mt: 1.5, color: "#7a8696", fontSize: "0.95rem" }}>
              Loading map...
            </Typography>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="warning" sx={{ borderRadius: "12px", mb: 1.5 }}>
                Could not load member locations. Please try again later.
              </Alert>
            )}

            <Box
              sx={{
                height: { xs: "50vh", sm: "55vh", md: "60vh", lg: "65vh" },
                minHeight: 350,
                borderRadius: "14px",
                backgroundColor: "#fbfcfe",
                border: "1px solid #eef2f7",
                p: 1,
                overflow: "hidden",
                "& .leaflet-container": {
                  height: "100%",
                  width: "100%",
                  borderRadius: "10px",
                  fontFamily: "inherit",
                },
                "& .leaflet-popup-content-wrapper": {
                  borderRadius: "12px",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                },
                "& .leaflet-popup-content": {
                  margin: "10px 12px",
                  color: "#223142",
                  fontSize: "0.92rem",
                },
              }}
            >
              <MapContainer
                center={[39.8283, -98.5795]}
                zoom={5}
                scrollWheelZoom={true}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                <MapBoundsController locations={locations} fitRef={fitRef} />

                <MarkerClusterGroup
                  chunkedLoading
                  maxClusterRadius={40}
                  disableClusteringAtZoom={13}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                  zoomToBoundsOnClick={true}
                  iconCreateFunction={(cluster) => {
                    const count = cluster.getChildCount();
                    const size = count >= 50 ? 52 : count >= 20 ? 46 : count >= 10 ? 40 : 34;
                    return L.divIcon({
                      html: `<div style="
                        width:${size}px;height:${size}px;
                        background:rgba(47,99,173,0.92);
                        border:3px solid #fff;
                        border-radius:50%;
                        display:flex;align-items:center;justify-content:center;
                        box-shadow:0 3px 10px rgba(0,0,0,0.25);
                        font-size:${count >= 50 ? 15 : 14}px;
                        font-weight:700;color:#fff;font-family:inherit;
                      ">${count}</div>`,
                      className: "",
                      iconSize: [size, size],
                      iconAnchor: [size / 2, size / 2],
                    });
                  }}
                >
                  {locations.map((loc, idx) => {
                    const status = STATUS_STYLES[loc.color] || STATUS_STYLES.default;
                    return (
                      <Marker
                        key={`${loc.ClubMemberCode}-${idx}`}
                        position={[loc.lat, loc.lng]}
                        icon={MARKER_ICONS[loc.color] || MARKER_ICONS.default}
                      >
                        <Popup>
                          <strong style={{ fontSize: "0.95rem" }}>
                            {loc.ClubMemberCode}
                          </strong>
                          <div style={{ marginTop: 4, color: "#6b778c", fontSize: "0.88rem" }}>
                            {loc.address}
                          </div>
                          <div style={{ marginTop: 6 }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: 6,
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                backgroundColor: status.bg,
                                color: status.color,
                              }}
                            >
                              {status.label}
                            </span>
                          </div>
                          <a
                            href={`/home/clubmembers/${loc.ClubMemberCode}`}
                            style={{
                              display: "block",
                              marginTop: 8,
                              color: "#2f63ad",
                              fontSize: "0.88rem",
                              fontWeight: 600,
                              textDecoration: "none",
                            }}
                          >
                            View Profile →
                          </a>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
              </MapContainer>
            </Box>

            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "#9eaab5", textAlign: "right" }}
            >
              Scroll to zoom · Click a cluster to expand · Individual markers appear at zoom 12+
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OpenStreetMapComponent;
